import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  HandCoins, RefreshCw, Loader2, FileSpreadsheet, FileText, Search,
  StickyNote, UserMinus, RotateCcw, Percent, ChevronRight, Clock, Calculator, Check,
  ImagePlus, X as XIcon, ZoomIn, ZoomOut, Download, Eye, Phone, Mail,
} from 'lucide-react';
import { fmtMoney } from './_prestamosUtils';

const estadoLoanColor = (e) => ({
  Mora: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
  Vigente: 'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300',
  Cancelado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
}[e] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300');

const cuotaBadge = (est) => {
  if (est === 'abonada') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Abonada</Badge>;
  if (est === 'mora') return <Badge className="bg-rose-100 text-rose-700 border-0">Mora</Badge>;
  return <Badge className="bg-blue-100 text-blue-700 border-0">Pendiente</Badge>;
};

// Visor 360° de una deuda: cabecera + composición de mora + cuotas con pagos.
const DetalleMora = ({ prestamoId, open, onClose }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [act, setAct] = useState(null);           // actividad declarada ARCA
  const [actLoading, setActLoading] = useState(false);
  const [waMail, setWaMail] = useState(null);      // email desde Waya: null=no buscado, ''=no encontrado, str=encontrado
  const [waMailLoading, setWaMailLoading] = useState(false);
  useEffect(() => {
    if (!open || !prestamoId) return;
    let cancel = false;
    setLoading(true); setD(null); setAct(null); setWaMail(null);
    (async () => {
      try {
        const r = await api.get(`/cobranzas/detalle/${prestamoId}`);
        if (!cancel) setD(r.data);
      } catch (e) {
        if (!cancel) { toast.error(e?.response?.data?.detail || 'No se pudo cargar el detalle de la deuda'); onClose(); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open, prestamoId]); // eslint-disable-line

  const consultarActividad = async () => {
    if (!d?.cuit) return;
    setActLoading(true);
    try {
      const r = await api.get(`/cobranzas/actividad/${String(d.cuit).replace(/\D/g, '')}`);
      if (r.data?.success) setAct(r.data);
      else { toast.error(r.data?.error || 'No se pudo obtener la actividad'); setAct({ success: false }); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo consultar ARCA');
      setAct({ success: false });
    } finally { setActLoading(false); }
  };

  const buscarEmailWaya = async () => {
    if (!d?.cuit) return;
    setWaMailLoading(true);
    try {
      const r = await api.get(`/cobranzas/email-waya/${String(d.cuit).replace(/\D/g, '')}`);
      if (r.data?.success && r.data.email) { setWaMail(r.data.email); toast.success('Email encontrado en Waya'); }
      else { setWaMail(''); toast.info('No se encontró email en Waya'); }
    } catch (e) {
      setWaMail(''); toast.error(e?.response?.data?.detail || 'No se pudo consultar Waya');
    } finally { setWaMailLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="cobranzas-detalle-sheet">
        <SheetHeader>
          <SheetTitle>Préstamo {prestamoId}</SheetTitle>
          <SheetDescription>Detalle de la deuda: cómo está compuesta la mora (cuotas, pagos y punitorios).</SheetDescription>
        </SheetHeader>
        {loading || !d?.cuit ? (
          <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="space-y-5 mt-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{d.nombre}</p>
                <Badge className={`${estadoLoanColor(d.estado)} border-0`}>{d.estado || '—'}</Badge>
              </div>
              <p className="font-mono text-xs text-slate-500 flex items-center gap-2">{d.cuit}{d.telefono ? <><span>·</span><Phone className="h-3 w-3" />{d.telefono}</> : null}</p>
              {d.email ? (
                <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5" data-testid="cobranzas-detalle-email"><Mail className="h-3 w-3" /><a href={`mailto:${d.email}`} className="hover:underline break-all">{d.email}</a></p>
              ) : waMail ? (
                <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5" data-testid="cobranzas-detalle-email"><Mail className="h-3 w-3" /><a href={`mailto:${waMail}`} className="hover:underline break-all">{waMail}</a><span className="text-[10px] text-slate-400">(Waya)</span></p>
              ) : (
                <div className="flex items-center gap-2 mt-0.5" data-testid="cobranzas-detalle-email-empty">
                  <span className="text-xs text-slate-400 flex items-center gap-1.5"><Mail className="h-3 w-3" />Sin email en el padrón</span>
                  {waMail === null && <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={buscarEmailWaya} disabled={waMailLoading} data-testid="cobranzas-detalle-email-waya-btn">{waMailLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Buscar en Waya'}</Button>}
                  {waMail === '' && <span className="text-[11px] text-amber-600">no encontrado en Waya</span>}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-0.5">{d.frecuencia} · {d.cantidad_cuotas} cuotas · Prestado {fmtMoney(d.monto_solicitado)}{d.tna ? ` · TNA ${d.tna}` : ''}</p>
              {d.fecha_solicitud && <p className="text-xs text-slate-400">Solicitado: {d.fecha_solicitud}{d.accion_realizada ? ` · ${d.accion_realizada}` : ''}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Ingreso realizado</p><p className="font-bold text-emerald-600">{fmtMoney(d.ingreso_realizado)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Ganancia esperada</p><p className="font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(d.ganancia_esperada)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">% Recupero</p><p className="font-semibold text-teal-600">{d.pct_realizado ?? 0}%</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Mora consolidada</p><p className="font-bold text-rose-600">{fmtMoney(d.total_mora)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Punitorios</p><p className="font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(d.punitorios_saldo)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Días mora</p><p className="font-semibold text-rose-600">{d.dias_mora || 0}</p></div>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="cobranzas-detalle-actividad">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Actividad declarada (ARCA)</p>
                {!act && (
                  <Button size="sm" variant="outline" className="h-7" onClick={consultarActividad} disabled={actLoading} data-testid="cobranzas-detalle-actividad-btn">
                    {actLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Consultar'}
                  </Button>
                )}
              </div>
              {act && act.success && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs text-slate-500">{act.razon_social || '—'} · {act.tipo_persona || ''}{act.monotributo ? ` · Monotributo ${act.monotributo}` : ''}</p>
                  {(act.actividades || []).length > 0 ? (
                    <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1">
                      {(act.actividades || []).slice(0, 8).map((a, i) => {
                        const principal = String(a.orden) === '1' || String(a.orden) === '1.0';
                        return (
                          <li key={i} className="flex items-center gap-2">
                            {principal && <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px] px-1.5 py-0">Principal</Badge>}
                            <span>{a.descripcion} {a.codigo ? <span className="text-slate-400">({a.codigo})</span> : null}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : <p className="text-sm text-slate-400">Sin actividades declaradas.</p>}
                </div>
              )}
              {act && !act.success && <p className="text-xs text-amber-600 mt-1">No se pudo obtener la actividad desde ARCA.</p>}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">Cuotas</p>
              <div className="space-y-2" data-testid="cobranzas-detalle-cuotas">
                {(d.cuotas || []).map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">{c.cuota || c.nro}</span>
                        {c.es_punitorio && <Badge className="ml-2 bg-purple-100 text-purple-700 border-0">Punitorios</Badge>}
                        <span className="ml-2 text-xs text-slate-400">vto {c.fecha_vto}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">Cuota {fmtMoney(c.total)}</span>
                        <span className={`text-sm font-semibold ${c.estado === 'mora' ? 'text-rose-600' : c.estado === 'pendiente' ? 'text-blue-600' : 'text-emerald-600'}`}>Saldo {fmtMoney(c.saldo)}</span>
                        {cuotaBadge(c.estado)}
                      </div>
                    </div>
                    {(c.pagos || []).length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-slate-100 dark:border-slate-700 space-y-0.5">
                        {c.pagos.map((p, j) => (
                          <div key={j} className="flex justify-between text-xs text-slate-500"><span>Pago {p.fecha}</span><span className="text-emerald-600">{fmtMoney(p.monto)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Muestra una imagen protegida por auth (la trae por el backend como blob).
const AuthImage = ({ adjId, className, onOpen }) => {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url; let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/cobranzas/adjunto/${adjId}`, { responseType: 'blob' });
        if (cancel) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } catch (e) { if (!cancel) setErr(true); }
    })();
    return () => { cancel = true; if (url) URL.revokeObjectURL(url); };
  }, [adjId]);
  if (err) return <div className={`${className} flex items-center justify-center bg-slate-100 text-[9px] text-slate-400`}>error</div>;
  if (!src) return <div className={`${className} flex items-center justify-center bg-slate-100`}><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  return <img src={src} alt="adjunto" className={className} onClick={onOpen ? () => onOpen() : undefined} />;
};

// Visor de imagen a pantalla completa con zoom y paneo.
const ImageLightbox = ({ adjId, onClose }) => {
  const [src, setSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    let url; let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/cobranzas/adjunto/${adjId}`, { responseType: 'blob' });
        if (cancel) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } catch (e) { /* noop */ }
    })();
    return () => { cancel = true; if (url) URL.revokeObjectURL(url); };
  }, [adjId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const clamp = (s) => Math.min(6, Math.max(0.25, s));
  const zoomIn = () => setScale((s) => clamp(s + 0.25));
  const zoomOut = () => setScale((s) => clamp(s - 0.25));
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };
  const onWheel = (e) => { setScale((s) => clamp(s + (e.deltaY < 0 ? 0.2 : -0.2))); };
  const onMouseDown = (e) => { if (scale <= 1) return; dragRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; setDragging(true); };
  const onMouseMove = (e) => { if (dragRef.current) setPos({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y }); };
  const endDrag = () => { dragRef.current = null; setDragging(false); };

  const btn = 'h-9 w-9 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors';

  return createPortal((
    <div className="fixed inset-0 z-[2000] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }} onClick={onClose} data-testid="cobranzas-lightbox">
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
        <button onClick={zoomOut} className={btn} title="Alejar" data-testid="cobranzas-lightbox-zoom-out"><ZoomOut className="h-5 w-5" /></button>
        <button onClick={reset} className="h-9 min-w-[64px] px-2 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold" title="Restablecer" data-testid="cobranzas-lightbox-reset">{Math.round(scale * 100)}%</button>
        <button onClick={zoomIn} className={btn} title="Acercar" data-testid="cobranzas-lightbox-zoom-in"><ZoomIn className="h-5 w-5" /></button>
        {src && <a href={src} download={`adjunto-${adjId}`} className={btn} title="Descargar" onClick={(e) => e.stopPropagation()} data-testid="cobranzas-lightbox-download"><Download className="h-5 w-5" /></a>}
        <button onClick={onClose} className={btn} title="Cerrar (Esc)" data-testid="cobranzas-lightbox-close"><XIcon className="h-5 w-5" /></button>
      </div>
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        {src ? (
          <img
            src={src}
            alt="adjunto"
            draggable={false}
            onDoubleClick={() => (scale > 1 ? reset() : setScale(2))}
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              transition: dragging ? 'none' : 'transform 0.12s ease-out',
              cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
            className="max-h-[88vh] max-w-[92vw] object-contain select-none"
          />
        ) : (
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        )}
      </div>
    </div>
  ), document.body);
};

// Logo oficial de WhatsApp (lucide no lo trae). Hereda el color con currentColor.
const WhatsAppIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.9 11.9 0 005.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411" />
  </svg>
);

const ESTADOS = [
  { v: 'nuevo', l: 'Nuevo', c: 'bg-slate-100 text-slate-600' },
  { v: 'en_gestion', l: 'En gestión', c: 'bg-blue-100 text-blue-700' },
  { v: 'promesa', l: 'Promesa de pago', c: 'bg-emerald-100 text-emerald-700' },
  { v: 'no_contactado', l: 'No contactado', c: 'bg-amber-100 text-amber-700' },
];
const estadoLabel = (v) => (ESTADOS.find((e) => e.v === v) || ESTADOS[0]).l;

const StatCard = ({ label, value, sub, testid, accent }) => (
  <Card className={`p-4 ${accent || 'border-slate-200 dark:border-slate-800'}`} data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </Card>
);

const Cobranzas = () => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'ADMINISTRADOR';

  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [ultimaAct, setUltimaAct] = useState(null);
  const [puedeResolverState, setPuedeResolverState] = useState(false);
  const puedeResolver = isAdmin || puedeResolverState;
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [quitaInput, setQuitaInput] = useState('50');
  const quitaNum = Math.max(0, Math.min(100, Number(quitaInput) || 0));
  const [vista, setVista] = useState('activas');
  const [excluidas, setExcluidas] = useState([]);

  const [notaRow, setNotaRow] = useState(null);
  const [notaAdd, setNotaAdd] = useState('');
  const [notaImgs, setNotaImgs] = useState([]);      // {id, filename, localUrl}
  const [notaUploading, setNotaUploading] = useState(false);
  const [lightboxAdj, setLightboxAdj] = useState(null);
  const [detRow, setDetRow] = useState(null);      // prestamo_id a ver en el visor 360°
  const notaFileRef = useRef(null);
  const [exclRow, setExclRow] = useState(null);
  const [exclMotivo, setExclMotivo] = useState('');
  const [waMensaje, setWaMensaje] = useState('');
  const [waPrefijo, setWaPrefijo] = useState('54');
  const [waCfgOpen, setWaCfgOpen] = useState(false);
  const [waCfgMsg, setWaCfgMsg] = useState('');
  const [waCfgPrefijo, setWaCfgPrefijo] = useState('54');

  const [refiRow, setRefiRow] = useState(null);      // grupo (persona) a refinanciar
  const [refiData, setRefiData] = useState(null);
  const [refiLoading, setRefiLoading] = useState(false);
  const [refiIncluirPun, setRefiIncluirPun] = useState(true);
  const [planElegidoIdx, setPlanElegidoIdx] = useState(null);
  const [eligiendo, setEligiendo] = useState(false);

  const abrirRefinanciacion = useCallback(async (g) => {
    setRefiRow(g);
    setRefiData(null);
    setRefiIncluirPun(true);
    setPlanElegidoIdx(null);
    setRefiLoading(true);
    try {
      const r = await api.get('/cobranzas/refinanciacion', { params: { cuit: g.cuit } });
      setRefiData(r.data);
    } catch (e) {
      toast.error('No se pudo calcular la refinanciación');
      setRefiRow(null);
    } finally {
      setRefiLoading(false);
    }
  }, []);

  const saldoConQuita = useCallback(
    (r) => (r.deuda_sin_interes || 0) + (r.punitorios || 0) * (1 - quitaNum / 100),
    [quitaNum],
  );

  // Fecha "DD/MM/YYYY" (o ISO) -> Date, para calcular la más vieja del grupo.
  const parseFecha = (s) => {
    const t = String(s || '').split(' ')[0];
    const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
    const d = new Date(t);
    return isNaN(d.getTime()) ? null : d;
  };
  const fechaMasVieja = (items, key) => {
    let best = null, bestStr = null;
    for (const x of items) {
      const d = parseFecha(x[key]);
      if (d && (!best || d < best)) { best = d; bestStr = x[key]; }
    }
    return bestStr || items.find((x) => x[key])?.[key] || null;
  };

  // Agrupa las deudas por persona (CUIT). Suma importes, toma la fecha más vieja
  // y el mayor días de mora. Cada préstamo individual queda en `items` (detalle expandible).
  const grupos = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.cuit || `__${r.prestamo_id}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const arr = [];
    for (const [key, items] of map.entries()) {
      const sum = (k) => items.reduce((a, x) => a + (Number(x[k]) || 0), 0);
      arr.push({
        key,
        items,
        count: items.length,
        prestamo_id: items[0].prestamo_id,
        cuit: items[0].cuit,
        nombre: items.find((x) => x.nombre)?.nombre || items[0].nombre,
        telefono: items.find((x) => x.telefono)?.telefono || '',
        dias_mora: Math.max(...items.map((x) => Number(x.dias_mora) || 0)),
        fecha_alta: fechaMasVieja(items, 'fecha_alta'),
        fecha_mora: fechaMasVieja(items, 'fecha_mora'),
        deuda_sin_interes: sum('deuda_sin_interes'),
        punitorios: sum('punitorios'),
        saldo_total: sum('saldo_total'),
      });
    }
    arr.sort((a, b) => b.saldo_total - a.saldo_total);
    return arr;
  }, [rows]);

  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const fmtFechaHora = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ');
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const WaButton = (r) => (
    r.telefono ? (
      <button
        type="button"
        onClick={() => window.open(waHref(r), '_blank', 'noopener,noreferrer')}
        title="Enviar WhatsApp"
        data-testid={`cobranzas-wa-${r.prestamo_id}`}
        style={{ backgroundColor: '#25D366' }}
        className="inline-flex items-center gap-1 h-6 pl-1.5 pr-2.5 rounded-full text-[11px] font-bold hover:opacity-90 active:scale-95 transition-all"
      >
        <WhatsAppIcon className="h-3.5 w-3.5" style={{ color: '#ffffff' }} />
        <span style={{ color: '#ffffff' }}>WhatsApp</span>
      </button>
    ) : null
  );

  const EstadoSelect = (r) => (
    <select value={r.estado_gestion} onChange={(e) => cambiarEstado(r, e.target.value)} className="text-xs border border-slate-200 dark:border-slate-700 rounded-md px-1.5 py-1.5 bg-white dark:bg-slate-900 w-32" data-testid={`cobranzas-estado-${r.prestamo_id}`}>
      {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
    </select>
  );

  const AccionesCell = (r) => (
    <div className="flex items-center justify-center gap-1.5">
      <button title="Ver detalle de la mora" onClick={() => setDetRow(r.prestamo_id)} data-testid={`cobranzas-ver-${r.prestamo_id}`}
        style={{ backgroundColor: '#e0f2fe' }}
        className="inline-flex items-center justify-center h-8 w-8 rounded-lg hover:opacity-80 transition-opacity">
        <Eye className="h-4 w-4" style={{ color: '#0284c7' }} />
      </button>
      <button title="Notas de seguimiento" onClick={() => { setNotaRow(r); setNotaAdd(''); setNotaImgs([]); }} data-testid={`cobranzas-nota-${r.prestamo_id}`}
        style={{ backgroundColor: '#fef3c7' }}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded-lg hover:opacity-80 transition-opacity">
        <StickyNote className="h-4 w-4" style={{ color: '#d97706' }} />
        {r.notas && r.notas.length > 0 && <span style={{ backgroundColor: '#d97706', color: '#fff' }} className="absolute -top-1.5 -right-1.5 text-[9px] rounded-full h-4 min-w-[16px] px-0.5 flex items-center justify-center">{r.notas.length}</span>}
      </button>
      {puedeResolver && (
        <button title="Sacar de gestión" onClick={() => { setExclRow(r); setExclMotivo(''); }} data-testid={`cobranzas-excluir-${r.prestamo_id}`}
          style={{ backgroundColor: '#fee2e2' }}
          className="inline-flex items-center justify-center h-8 w-8 rounded-lg hover:opacity-80 transition-opacity">
          <UserMinus className="h-4 w-4" style={{ color: '#dc2626' }} />
        </button>
      )}
    </div>
  );

  const RefiButton = (g) => (
    <button title="Simular refinanciación" onClick={() => abrirRefinanciacion(g)} data-testid={`cobranzas-refi-${g.cuit}`}
      style={{ backgroundColor: '#e0e7ff' }}
      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg hover:opacity-80 transition-opacity">
      <Calculator className="h-4 w-4" style={{ color: '#4f46e5' }} />
      <span style={{ color: '#4f46e5' }} className="text-[11px] font-semibold">Refi</span>
    </button>
  );


  const soloDigitos = (t) => String(t || '').replace(/\D/g, '').replace(/^0+/, '');
  const soloFecha = (t) => (String(t || '').split(' ')[0] || '—');
  const fillMsg = useCallback((r) => (waMensaje || '')
    .replaceAll('{nombre}', r.nombre || '')
    .replaceAll('{saldo_total}', fmtMoney(r.saldo_total))
    .replaceAll('{saldo_con_quita}', fmtMoney(saldoConQuita(r)))
    .replaceAll('{dias_mora}', String(r.dias_mora ?? ''))
    .replaceAll('{deuda_sin_interes}', fmtMoney(r.deuda_sin_interes))
    .replaceAll('{punitorios}', fmtMoney(r.punitorios)), [waMensaje, saldoConQuita]);
  const waHref = useCallback((r) => `https://wa.me/${waPrefijo}${soloDigitos(r.telefono)}?text=${encodeURIComponent(fillMsg(r))}`, [waPrefijo, fillMsg]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/cobranzas/config');
      const qd = r.data?.quita_default ?? 50;
      setQuitaInput(String(qd));
      setWaMensaje(r.data?.wa_mensaje || '');
      setWaPrefijo(r.data?.wa_prefijo || '54');
    } catch (e) { /* usa default */ }
  }, []);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await api.get('/cobranzas/deudas', { params: { vista: 'activas', q: query ?? q } });
      setRows(r.data?.rows || []);
      setResumen(r.data?.resumen || null);
      setUltimaAct(r.data?.ultima_actualizacion || null);
      setPuedeResolverState(!!r.data?.puede_resolver);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar las deudas');
    } finally {
      setLoading(false);
    }
  }, [q]);

  const elegirPlan = useCallback(async (o, idx) => {
    if (!refiRow || !refiData) return;
    const inc = refiIncluirPun;
    const monto = inc ? refiData.monto.con_punitorios : refiData.monto.sin_punitorios;
    const cuota = inc ? o.cuota_con_pun : o.cuota_sin_pun;
    const total = inc ? o.total_con_pun : o.total_sin_pun;
    const texto =
      `Plan de refinanciación elegido: ${o.cuotas} cuota(s) ${o.frecuencia} ` +
      `(plazo ${o.plazo} días) · TNA ${o.tna}% · monto a refinanciar ${fmtMoney(monto)}` +
      `${inc ? '' : ' (sin punitorios)'} · valor cuota ${fmtMoney(cuota)} · total a pagar ${fmtMoney(total)}.`;
    const prestamo_ids = (refiRow.items || []).map((it) => it.prestamo_id).filter(Boolean);
    setEligiendo(true);
    try {
      await api.post('/cobranzas/refinanciacion/elegir', {
        prestamo_ids,
        texto,
        plan: {
          cuit: refiData.cuit, cuotas: o.cuotas, frecuencia: o.frecuencia, plazo: o.plazo,
          tna: o.tna, incluir_punitorios: inc, monto, cuota, total,
        },
      });
      setPlanElegidoIdx(idx);
      toast.success('Plan registrado en las notas del cliente');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo registrar el plan');
    } finally {
      setEligiendo(false);
    }
  }, [refiRow, refiData, refiIncluirPun, load]);


  const loadExcluidas = useCallback(async () => {
    try {
      const r = await api.get('/cobranzas/deudas', { params: { vista: 'excluidas' } });
      setExcluidas(r.data?.rows || []);
    } catch (e) { /* admin only */ }
  }, []);

  useEffect(() => { loadConfig(); load(''); }, []); // eslint-disable-line
  useEffect(() => { if (vista === 'excluidas') loadExcluidas(); }, [vista, loadExcluidas]);

  const aplicarQuita = async () => {
    const val = quitaNum;
    setQuitaInput(String(val));
    if (isAdmin) {
      try {
        await api.put('/cobranzas/config', { quita_default: val });
        toast.success(`Quita por defecto guardada: ${val}% sobre punitorios`);
      } catch (e) {
        toast.error('No se pudo guardar la quita por defecto');
      }
    } else {
      toast.success(`Quita aplicada: ${val}% sobre punitorios`);
    }
  };

  const subirImagenesNota = useCallback(async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type && f.type.startsWith('image/'));
    if (!arr.length) return;
    setNotaUploading(true);
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/cobranzas/nota/imagen', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.id) {
          setNotaImgs((prev) => [...prev, { id: r.data.id, filename: r.data.filename, localUrl: URL.createObjectURL(f) }]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'No se pudo subir la imagen');
      }
    }
    setNotaUploading(false);
  }, []);

  const onPasteNota = useCallback((e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    const imgs = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length) { e.preventDefault(); subirImagenesNota(imgs); }
  }, [subirImagenesNota]);

  const quitarImgPendiente = (id) => setNotaImgs((prev) => prev.filter((i) => i.id !== id));

  const agregarNota = async () => {
    const texto = notaAdd.trim();
    const imagenes = notaImgs.map((i) => i.id);
    if (!texto && imagenes.length === 0) return;
    try {
      const r = await api.post('/cobranzas/nota', { prestamo_id: notaRow.prestamo_id, texto, imagenes });
      const nueva = r.data?.nota;
      setNotaRow((prev) => (prev ? { ...prev, notas: [...(prev.notas || []), nueva] } : prev));
      setRows((prev) => prev.map((x) => (x.prestamo_id === notaRow.prestamo_id
        ? { ...x, notas: [...(x.notas || []), nueva], nota: nueva.texto } : x)));
      setNotaAdd('');
      setNotaImgs([]);
      toast.success('Nota agregada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo agregar la nota');
    }
  };

  const openWaCfg = () => { setWaCfgMsg(waMensaje); setWaCfgPrefijo(waPrefijo); setWaCfgOpen(true); };
  const saveWaCfg = async () => {
    try {
      await api.put('/cobranzas/wa-config', { mensaje: waCfgMsg, prefijo: waCfgPrefijo });
      setWaMensaje(waCfgMsg);
      setWaPrefijo((waCfgPrefijo || '54').replace(/\D/g, '') || '54');
      setWaCfgOpen(false);
      toast.success('Mensaje de WhatsApp guardado');
    } catch (e) {
      toast.error('No se pudo guardar el mensaje');
    }
  };

  const cambiarEstado = async (row, estado) => {
    setRows((prev) => prev.map((r) => (r.prestamo_id === row.prestamo_id ? { ...r, estado_gestion: estado } : r)));
    try {
      await api.post('/cobranzas/gestion', { prestamo_id: row.prestamo_id, estado });
    } catch (e) {
      toast.error('No se pudo actualizar el estado');
      load();
    }
  };

  const confirmarExclusion = async () => {
    try {
      await api.post('/cobranzas/excluir', { prestamo_id: exclRow.prestamo_id, motivo: exclMotivo });
      toast.success('Deuda sacada de gestión');
      setExclRow(null);
      setExclMotivo('');
      load();
      loadExcluidas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo sacar de gestión');
    }
  };

  const restaurar = async (row) => {
    try {
      await api.post('/cobranzas/restaurar', { prestamo_id: row.prestamo_id });
      toast.success('Deuda restaurada a gestión');
      loadExcluidas();
      load();
    } catch (e) {
      toast.error('No se pudo restaurar');
    }
  };

  const exportar = async (formato) => {
    try {
      const r = await api.get('/cobranzas/export', { params: { formato, q, quita: quitaNum }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cobranzas.${formato === 'xlsx' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    }
  };

  return (
    <div className="p-4 lg:p-6 pt-20 w-full" data-testid="cobranzas-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><HandCoins className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Cobranzas</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-slate-500">Gestión de deuda · recupero de mora</p>
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="cobranzas-ultima-actualizacion" title="Última actualización de la cartera desde Waya">
                <Clock className="h-3 w-3" />Actualizado: {fmtFechaHora(ultimaAct)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => exportar('xlsx')} data-testid="cobranzas-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => exportar('pdf')} data-testid="cobranzas-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => load()} data-testid="cobranzas-refresh-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Actualizar</Button>
        </div>
      </div>

      {/* Controles: búsqueda + quita */}
      <Card className="p-4 mb-4 border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="text-xs text-slate-500">Buscar (CUIT o nombre)</label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Ej. Leonardo / 20-..." className="pl-8 h-9" data-testid="cobranzas-buscar" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 flex items-center gap-1"><Percent className="h-3 w-3" />Quita sobre punitorios</label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" min={0} max={100} value={quitaInput} onChange={(e) => setQuitaInput(e.target.value)} className="h-9 w-24" data-testid="cobranzas-quita-input" />
              <span className="text-sm text-slate-400">%</span>
              <Button size="sm" variant="outline" onClick={aplicarQuita} data-testid="cobranzas-quita-aplicar">{isAdmin ? 'Guardar y aplicar' : 'Aplicar'}</Button>
            </div>
          </div>
          <Button size="sm" onClick={() => load()} className="bg-slate-700 hover:bg-slate-800 text-white h-9" data-testid="cobranzas-buscar-btn">Buscar</Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={openWaCfg} className="h-9" data-testid="cobranzas-wa-config-btn"><WhatsAppIcon className="h-4 w-4 mr-1.5 text-emerald-600" />Mensaje WhatsApp</Button>
          )}
        </div>
      </Card>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="Deudas en gestión" value={resumen.en_gestion} sub={`${resumen.excluidas} resueltas por fuera`} testid="cobranzas-kpi-engestion" accent="border-teal-200 bg-teal-50/40 dark:bg-teal-950/10" />
          <StatCard label="Total a reclamar" value={fmtMoney(resumen.total_a_reclamar)} sub={`Cuotas ${fmtMoney(resumen.total_deuda_sin_interes)} + Punit. ${fmtMoney(resumen.total_punitorios)}`} testid="cobranzas-kpi-total" />
          <StatCard label={`Total con quita (${quitaNum}%)`} value={fmtMoney(rows.reduce((a, r) => a + saldoConQuita(r), 0))} sub="Quita aplicada a punitorios" testid="cobranzas-kpi-quita" />
          <StatCard label="Promesas de pago" value={resumen.por_estado_gestion?.promesa || 0} sub={`${resumen.por_estado_gestion?.en_gestion || 0} en gestión`} testid="cobranzas-kpi-promesas" />
        </div>
      )}

      <Tabs value={vista} onValueChange={setVista} className="w-full" data-testid="cobranzas-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="activas" data-testid="cobranzas-tab-activas">En gestión {resumen ? `(${resumen.en_gestion})` : ''}</TabsTrigger>
          {puedeResolver && <TabsTrigger value="excluidas" data-testid="cobranzas-tab-excluidas">Resueltas por fuera {resumen ? `(${resumen.excluidas})` : ''}</TabsTrigger>}
        </TabsList>

        {/* LISTA ACTIVA */}
        <TabsContent value="activas" data-testid="cobranzas-content-activas">
          {loading ? (
            <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center text-slate-400 py-16" data-testid="cobranzas-empty"><HandCoins className="h-10 w-10 mx-auto mb-3 opacity-40" />No hay deudas en gestión.</div>
          ) : (
            <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1180px]">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="w-8 px-1 py-2.5"></th>
                      <th className="text-left px-2.5 py-2.5 font-medium">CUIL/DNI · Nombre</th>
                      <th className="text-left px-2.5 py-2.5 font-medium">Teléfono</th>
                      <th className="text-center px-2.5 py-2.5 font-medium">Días<br />mora</th>
                      <th className="text-left px-2.5 py-2.5 font-medium">Alta / Mora</th>
                      <th className="text-right px-2.5 py-2.5 font-medium">Deuda<br />s/int.</th>
                      <th className="text-right px-2.5 py-2.5 font-medium">Intereses<br />(punit.)</th>
                      <th className="text-right px-2.5 py-2.5 font-medium">Total a<br />reclamar</th>
                      <th className="text-right px-2.5 py-2.5 font-medium">Con quita</th>
                      <th className="text-left px-2.5 py-2.5 font-medium">Estado</th>
                      <th className="text-center px-2.5 py-2.5 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => {
                      const multi = g.count > 1;
                      const abierto = !!expanded[g.key];
                      return (
                        <React.Fragment key={g.key}>
                          <tr className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition-colors" data-testid={`cobranzas-row-${g.prestamo_id}`}>
                            <td className="px-1 py-2 align-top text-center">
                              {multi && (
                                <button type="button" onClick={() => toggleExpand(g.key)} title={abierto ? 'Contraer' : 'Ver préstamos'} data-testid={`cobranzas-expand-${g.cuit}`}
                                  className="inline-flex items-center justify-center h-6 w-6 rounded-md hover:bg-slate-200/70 dark:hover:bg-slate-700 transition-colors">
                                  <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${abierto ? 'rotate-90' : ''}`} />
                                </button>
                              )}
                            </td>
                            <td className="px-2.5 py-2 align-top">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-800 dark:text-slate-100 leading-tight">{g.nombre || '—'}</p>
                                {multi && <Badge className="bg-teal-100 text-teal-700 border-0 text-[10px] px-1.5">{g.count} préstamos</Badge>}
                              </div>
                              <p className="font-mono text-[11px] text-slate-500">{g.cuit}</p>
                            </td>
                            <td className="px-2.5 py-2 align-top" data-testid={`cobranzas-tel-${g.prestamo_id}`}>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{g.telefono || '—'}</span>
                                {WaButton(g)}
                              </div>
                            </td>
                            <td className="px-2.5 py-2 text-center align-top"><Badge className="bg-rose-100 text-rose-700 border-0">{g.dias_mora}</Badge></td>
                            <td className="px-2.5 py-2 text-xs whitespace-nowrap align-top">
                              <div className="text-slate-600 dark:text-slate-300">{soloFecha(g.fecha_alta)}</div>
                              <div className="text-rose-500">{soloFecha(g.fecha_mora)}</div>
                            </td>
                            <td className="px-2.5 py-2 text-right text-slate-700 dark:text-slate-200 tabular-nums align-top">{fmtMoney(g.deuda_sin_interes)}</td>
                            <td className="px-2.5 py-2 text-right text-amber-600 tabular-nums align-top">{fmtMoney(g.punitorios)}</td>
                            <td className="px-2.5 py-2 text-right font-semibold text-rose-600 tabular-nums align-top" data-testid={`cobranzas-total-${g.prestamo_id}`}>{fmtMoney(g.saldo_total)}</td>
                            <td className="px-2.5 py-2 text-right font-semibold text-emerald-600 tabular-nums align-top" data-testid={`cobranzas-quita-${g.prestamo_id}`}>{fmtMoney(saldoConQuita(g))}</td>
                            <td className="px-2.5 py-2 align-top">
                              {multi
                                ? <button type="button" onClick={() => toggleExpand(g.key)} className="text-xs text-teal-600 hover:underline" data-testid={`cobranzas-vermas-${g.cuit}`}>{abierto ? 'Ocultar detalle' : 'Ver detalle'}</button>
                                : EstadoSelect(g.items[0])}
                            </td>
                            <td className="px-2.5 py-2 align-top">
                              <div className="flex items-center justify-center gap-1.5">
                                {RefiButton(g)}
                                {!multi && AccionesCell(g.items[0])}
                              </div>
                            </td>
                          </tr>
                          {multi && abierto && g.items.map((it) => (
                            <tr key={`c-${it.prestamo_id}`} className="bg-slate-50/60 dark:bg-slate-800/20 border-t border-slate-100 dark:border-slate-800/60" data-testid={`cobranzas-subrow-${it.prestamo_id}`}>
                              <td className="px-1 py-2"></td>
                              <td className="px-2.5 py-2 align-top pl-6">
                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" />Préstamo <span className="font-mono">#{it.prestamo_id}</span></span>
                              </td>
                              <td className="px-2.5 py-2 align-top">{WaButton(it)}</td>
                              <td className="px-2.5 py-2 text-center align-top"><Badge className="bg-rose-50 text-rose-600 border-0">{it.dias_mora}</Badge></td>
                              <td className="px-2.5 py-2 text-xs whitespace-nowrap align-top">
                                <div className="text-slate-600 dark:text-slate-300">{soloFecha(it.fecha_alta)}</div>
                                <div className="text-rose-500">{soloFecha(it.fecha_mora)}</div>
                              </td>
                              <td className="px-2.5 py-2 text-right text-slate-700 dark:text-slate-200 tabular-nums align-top">{fmtMoney(it.deuda_sin_interes)}</td>
                              <td className="px-2.5 py-2 text-right text-amber-600 tabular-nums align-top">{fmtMoney(it.punitorios)}</td>
                              <td className="px-2.5 py-2 text-right font-semibold text-rose-600 tabular-nums align-top">{fmtMoney(it.saldo_total)}</td>
                              <td className="px-2.5 py-2 text-right font-semibold text-emerald-600 tabular-nums align-top">{fmtMoney(saldoConQuita(it))}</td>
                              <td className="px-2.5 py-2 align-top">{EstadoSelect(it)}</td>
                              <td className="px-2.5 py-2 align-top">{AccionesCell(it)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* EXCLUIDAS (requiere acción "Eliminar" en Cobranzas) */}
        {puedeResolver && (
          <TabsContent value="excluidas" data-testid="cobranzas-content-excluidas">
            {excluidas.length === 0 ? (
              <div className="text-center text-slate-400 py-16" data-testid="cobranzas-excluidas-empty"><RotateCcw className="h-10 w-10 mx-auto mb-3 opacity-40" />No hay deudas resueltas por fuera.</div>
            ) : (
              <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-medium">ID</th>
                        <th className="text-left px-3 py-2.5 font-medium">CUIL/DNI · Nombre</th>
                        <th className="text-left px-3 py-2.5 font-medium">Teléfono</th>
                        <th className="text-right px-3 py-2.5 font-medium">Saldo (al excluir)</th>
                        <th className="text-left px-3 py-2.5 font-medium">Motivo</th>
                        <th className="text-left px-3 py-2.5 font-medium">Sacada por</th>
                        <th className="text-center px-3 py-2.5 font-medium">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excluidas.map((r) => (
                        <tr key={r.prestamo_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`cobranzas-excl-row-${r.prestamo_id}`}>
                          <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.prestamo_id}</td>
                          <td className="px-3 py-2.5"><p className="font-medium text-slate-800 dark:text-slate-100">{r.nombre || '—'}</p><p className="font-mono text-xs text-slate-500">{r.cuit}</p></td>
                          <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{r.telefono || '—'}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(r.saldo_total)}</td>
                          <td className="px-3 py-2.5 text-slate-600 text-xs">{r.motivo_exclusion || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-500 text-xs">{r.excluido_por || '—'}<br /><span className="text-slate-400">{(r.excluido_at || '').slice(0, 10)}</span></td>
                          <td className="px-3 py-2.5 text-center">
                            <Button size="sm" variant="outline" onClick={() => restaurar(r)} data-testid={`cobranzas-restaurar-${r.prestamo_id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restaurar</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Notas de seguimiento (historial) */}
      <Dialog open={!!notaRow} onOpenChange={(o) => !o && setNotaRow(null)}>
        <DialogContent data-testid="cobranzas-nota-dialog">
          <DialogHeader><DialogTitle>Seguimiento · {notaRow?.nombre}</DialogTitle>
            <DialogDescription>Historial de notas de gestión. Cada nota queda registrada con fecha y usuario.</DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-y-auto space-y-2 pr-1" data-testid="cobranzas-nota-historial">
            {(notaRow?.notas || []).length === 0 ? (
              <p className="text-sm text-slate-400">Todavía no hay notas. Agregá la primera abajo.</p>
            ) : (
              notaRow.notas.slice().reverse().map((n, i) => (
                <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-800 p-2" data-testid={`cobranzas-nota-item-${i}`}>
                  {n.texto ? <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{n.texto}</p> : null}
                  {(n.imagenes && n.imagenes.length > 0) && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid={`cobranzas-nota-imgs-${i}`}>
                      {n.imagenes.map((adjId) => (
                        <AuthImage
                          key={adjId}
                          adjId={adjId}
                          onOpen={() => setLightboxAdj(adjId)}
                          className="h-16 w-16 object-cover rounded-md border border-slate-200 cursor-zoom-in hover:opacity-80 transition-opacity"
                        />
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">{(n.fecha || '').slice(0, 16).replace('T', ' ')} · {n.autor || ''}</p>
                </div>
              ))
            )}
          </div>

          {/* Miniaturas de imágenes pendientes (aún sin guardar) */}
          {notaImgs.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="cobranzas-nota-pendientes">
              {notaImgs.map((img) => (
                <div key={img.id} className="relative">
                  <img src={img.localUrl} alt={img.filename} className="h-16 w-16 object-cover rounded-md border border-teal-300" />
                  <button
                    type="button"
                    onClick={() => quitarImgPendiente(img.id)}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center hover:bg-red-600"
                    data-testid={`cobranzas-nota-quitar-img-${img.id}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Textarea
            value={notaAdd}
            onChange={(e) => setNotaAdd(e.target.value)}
            onPaste={onPasteNota}
            rows={3}
            placeholder="Nueva nota: ej. Llamé, prometió pagar el viernes… (podés pegar una imagen con Ctrl+V)"
            data-testid="cobranzas-nota-text"
          />
          <input
            ref={notaFileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={(e) => { subirImagenesNota(e.target.files); e.target.value = ''; }}
            data-testid="cobranzas-nota-file-input"
          />
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => notaFileRef.current && notaFileRef.current.click()}
              disabled={notaUploading}
              data-testid="cobranzas-nota-adjuntar"
            >
              {notaUploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />}
              Adjuntar imagen
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNotaRow(null)}>Cerrar</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={agregarNota} disabled={notaUploading} data-testid="cobranzas-nota-guardar">Agregar nota</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visor de imagen con zoom (lightbox) */}
      {lightboxAdj && <ImageLightbox adjId={lightboxAdj} onClose={() => setLightboxAdj(null)} />}

      <DetalleMora prestamoId={detRow} open={!!detRow} onClose={() => setDetRow(null)} />

      {/* Dialog config Mensaje WhatsApp (admin) */}
      <Dialog open={waCfgOpen} onOpenChange={setWaCfgOpen}>
        <DialogContent data-testid="cobranzas-wa-dialog">
          <DialogHeader><DialogTitle>Mensaje de WhatsApp</DialogTitle>
            <DialogDescription>
              Mensaje predeterminado para todos. Variables: {'{nombre}'} · {'{saldo_total}'} · {'{saldo_con_quita}'} · {'{dias_mora}'} · {'{deuda_sin_interes}'} · {'{punitorios}'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Prefijo de país (sin +)</label>
              <Input value={waCfgPrefijo} onChange={(e) => setWaCfgPrefijo(e.target.value.replace(/\D/g, ''))} className="h-9 w-32 mt-1 font-mono" data-testid="cobranzas-wa-prefijo" />
              <p className="text-[11px] text-slate-400 mt-1">Argentina: <strong>54</strong>. Si WhatsApp no abre el chat correcto para celulares, probá <strong>549</strong>.</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">Mensaje</label>
              <Textarea value={waCfgMsg} onChange={(e) => setWaCfgMsg(e.target.value)} rows={5} className="mt-1" data-testid="cobranzas-wa-mensaje" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaCfgOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveWaCfg} data-testid="cobranzas-wa-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Excluir (admin) */}
      <Dialog open={!!exclRow} onOpenChange={(o) => !o && setExclRow(null)}>
        <DialogContent data-testid="cobranzas-excluir-dialog">
          <DialogHeader><DialogTitle>Sacar de gestión · {exclRow?.nombre}</DialogTitle>
            <DialogDescription>La deuda saldrá de la lista del cobrador (por ej. si se arregló por otro lado). Podés restaurarla luego.</DialogDescription>
          </DialogHeader>
          <Input value={exclMotivo} onChange={(e) => setExclMotivo(e.target.value)} placeholder="Motivo (opcional): resuelto por caja, pagó por transferencia…" data-testid="cobranzas-excluir-motivo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExclRow(null)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmarExclusion} data-testid="cobranzas-excluir-confirmar">Sacar de gestión</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Refinanciación (simulación consolidada) */}
      <Dialog open={!!refiRow} onOpenChange={(o) => !o && setRefiRow(null)}>
        <DialogContent className="max-w-2xl" data-testid="cobranzas-refi-dialog">
          <DialogHeader>
            <DialogTitle>Refinanciación · {refiRow?.nombre || refiData?.nombre}</DialogTitle>
            <DialogDescription>
              Simulación consolidada de toda la deuda de la persona con la TNA y los plazos de su nivel de riesgo (sistema francés).
            </DialogDescription>
          </DialogHeader>

          {refiLoading || !refiData ? (
            <div className="flex items-center justify-center py-10 text-slate-400" data-testid="cobranzas-refi-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Calculando opciones…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cabecera: nivel + monto */}
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge className="bg-indigo-100 text-indigo-700 border-0" data-testid="cobranzas-refi-nivel">
                  Nivel: {refiData.nivel || '—'}{refiData.nivel_es_default ? ' (predeterminado)' : ''}
                </Badge>
                <Badge className="bg-slate-100 text-slate-600 border-0">{refiData.cantidad_prestamos} préstamo(s)</Badge>
                {refiData.monto_max_nivel != null && (
                  <Badge className="bg-slate-100 text-slate-600 border-0">Monto máx nivel: {fmtMoney(refiData.monto_max_nivel)}</Badge>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 bg-slate-50/60 dark:bg-slate-800/30">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-slate-500">Monto a refinanciar</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="cobranzas-refi-monto">
                      {fmtMoney(refiIncluirPun ? refiData.monto.con_punitorios : refiData.monto.sin_punitorios)}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Capital {fmtMoney(refiData.monto.capital)} + Punitorios {fmtMoney(refiData.monto.punitorios)}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="cobranzas-refi-incluir-pun">
                    <input type="checkbox" checked={refiIncluirPun} onChange={(e) => setRefiIncluirPun(e.target.checked)}
                      className="h-4 w-4 accent-indigo-600" />
                    <span className="text-slate-600 dark:text-slate-300">Incluir punitorios</span>
                  </label>
                </div>
              </div>

              {refiData.sin_nivel || refiData.opciones.length === 0 ? (
                <p className="text-sm text-amber-600 py-4" data-testid="cobranzas-refi-sin-opciones">
                  No hay configuración de plazos/TNA para el nivel de este cliente. Corré la automatización
                  "Préstamos — Niveles de Riesgo y TNA" para actualizar los niveles.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-sm" data-testid="cobranzas-refi-tabla">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-center px-3 py-2 font-medium">Cuotas</th>
                        <th className="text-left px-3 py-2 font-medium">Frecuencia</th>
                        <th className="text-center px-3 py-2 font-medium">Plazo</th>
                        <th className="text-center px-3 py-2 font-medium">TNA</th>
                        <th className="text-right px-3 py-2 font-medium">Valor cuota</th>
                        <th className="text-right px-3 py-2 font-medium">Total a pagar</th>
                        <th className="text-center px-3 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {refiData.opciones.map((o, i) => {
                        const elegido = planElegidoIdx === i;
                        return (
                        <tr key={i}
                          className={`border-t border-slate-100 dark:border-slate-800 transition-colors ${elegido ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-indigo-50/40 dark:hover:bg-slate-800/30'}`}
                          data-testid={`cobranzas-refi-opt-${i}`}>
                          <td className="px-3 py-2 text-center font-semibold">{o.cuotas}</td>
                          <td className="px-3 py-2">{o.frecuencia}</td>
                          <td className="px-3 py-2 text-center text-slate-500">{o.plazo} d</td>
                          <td className="px-3 py-2 text-center text-slate-500">{o.tna}%</td>
                          <td className="px-3 py-2 text-right font-semibold text-indigo-700 tabular-nums">
                            {fmtMoney(refiIncluirPun ? o.cuota_con_pun : o.cuota_sin_pun)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200 tabular-nums">
                            {fmtMoney(refiIncluirPun ? o.total_con_pun : o.total_sin_pun)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {elegido ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold" data-testid={`cobranzas-refi-elegido-${i}`}>
                                <Check className="h-3.5 w-3.5" />Elegido
                              </span>
                            ) : (
                              <button type="button" disabled={eligiendo} onClick={() => elegirPlan(o, i)}
                                data-testid={`cobranzas-refi-elegir-${i}`}
                                className="inline-flex items-center h-7 px-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold transition-colors">
                                Elegir
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefiRow(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cobranzas;
